import React, {
  memo,
  useReducer,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { withPopUp, MeasurableView } from '@redlink/components/atoms';
import {
  LinkTemplateList,
  LinkTemplateActions,
} from '@redlink/components/muscles';
import { usePlan } from '@redlink/components/molecules';
import { useLocale } from '@redlink/assets/locale';
import { DataEntryModalTemplate } from '@redlink/components/templates';
import { batchNewExternalLinksQuery, useMutation } from '@redlink/services';
import { Form } from '@redlink/components/organisms';
import { useValues } from '@redlink/assets/values';
import { initialState, reducer, types } from './store';
import { freshCache } from './cache';

export const LinkTemplatesEditPage = withPopUp(
  memo(({ onDismiss, initialSelectedItemIndex = 0, pageName }) => {
    const { dictionary } = useLocale();
    const { staticData } = useValues();
    const sectionsLayout = useRef([]);
    const scrollRef = useRef({});
    const { showPlanDialog } = usePlan();

    const transformedTemplates = useMemo(() => {
      return staticData.linkTemplates.reduce((prev, { title, items }) => {
        const transformedItems = items.map((item) => ({
          parent: title,
          ...item,
        }));
        return [...prev, ...transformedItems];
      }, []);
    }, []);

    const [{ selectedItemIndex, items: mergedItems }, dispatch] = useReducer(
      reducer,
      initialState({
        selectedItemIndex: initialSelectedItemIndex,
        items: transformedTemplates,
      })
    );

    const [
      submitBatchExternalLinks,
      { loading: submitBatchExternalLinksIsLoading },
    ] = useMutation(batchNewExternalLinksQuery, {
      done: () => onDismiss(),
      update: freshCache({ pageName }),
      limited: ({ maxCount }) => {
        onDismiss && onDismiss();
        showPlanDialog();
      },
    });

    useEffect(() => {
      setTimeout(() => {
        const anchorPointY = sectionsLayout.current
          .slice(0, initialSelectedItemIndex)
          .reduce((prev, current) => prev + current.height, 0);
        scrollRef.current.getNode().scrollTo({ y: anchorPointY });
      }, 500);
    }, [scrollRef]);

    const onPressNavigationItem = useCallback(
      (key, index) => {
        dispatch({
          type: types.SET_SELECTED_ITEM_INDEX,
          payload: index,
        });

        const anchorPointY = sectionsLayout.current
          .slice(0, index)
          .reduce((prev, current) => prev + current.height, 0);
        scrollRef.current.getNode().scrollTo({ y: anchorPointY });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [scrollRef, sectionsLayout, mergedItems]
    );

    const onLayoutSection = useCallback(
      (index, layout) => {
        sectionsLayout.current[index] = layout;
      },
      [sectionsLayout]
    );

    return (
      <DataEntryModalTemplate>
        <>
          <Form
            {...{ scrollRef }}
            isBusy={submitBatchExternalLinksIsLoading}
            HeaderComponent={
              <LinkTemplateActions
                {...{ selectedItemIndex }}
                onItemPressed={onPressNavigationItem}
              />
            }
            actions={[
              {
                title: dictionary.cancel,
                variant: 'secondary',
                onPress: onDismiss,
              },
              {
                title: dictionary.add,
                variant: 'primary',
                onPress: () => {
                  const normalizedData = mergedItems
                    .filter(({ value: { raw = '' } = {} }) => raw.trim() !== '')
                    .map(({ value: { raw, ...rest } }) => rest);

                  submitBatchExternalLinks({
                    links: normalizedData,
                  });
                },
              },
            ]}
          >
            {staticData.linkTemplates.map(({ items, title }, index) => {
              const filteredItems = [
                ...mergedItems.filter(({ parent }) => parent === title),
              ];

              return (
                <MeasurableView
                  onMeasure={(layout) => onLayoutSection(index, layout)}
                >
                  <LinkTemplateList
                    data={filteredItems}
                    title={dictionary[title]}
                    onChange={(value) => {
                      const restItems = mergedItems.filter(
                        (p) => p.parent !== title
                      );
                      dispatch({
                        type: types.SET_ITEMS,
                        payload: [...restItems, ...value],
                      });
                    }}
                  />
                </MeasurableView>
              );
            })}
          </Form>
        </>
      </DataEntryModalTemplate>
    );
  })
);
